import { X, Edit, Radio, Music, MapPin, Plus } from 'lucide-react';
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

type Zone = {
  id: string;
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  playlist_id: string | null;
  shape_type: string;
  user_id: string;
};

type AudioPin = {
  id: string;
  title: string;
  description: string;
  youtube_video_id: string;
  latitude: number;
  longitude: number;
};

type ZonePlayerProps = {
  zone: Zone;
  zones: Zone[];
  onClose: () => void;
  onEdit?: () => void;
  onZoneSelect?: (zone: Zone) => void;
  canEdit?: boolean;
  onAddSoundDrop?: (lat: number, lng: number) => void;
};

export function ZonePlayer({ zone, zones, onClose, onEdit, onZoneSelect, canEdit, onAddSoundDrop }: ZonePlayerProps) {
  const [currentZone, setCurrentZone] = useState(zone);
  const [zonePins, setZonePins] = useState<AudioPin[]>([]);
  const [selectedPin, setSelectedPin] = useState<AudioPin | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const showAddButton = (zone as any)._clickedFrom === 'fill';

  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  useEffect(() => {
    setCurrentZone(zone);
    fetchZonePins(zone.id);
  }, [zone]);

  const fetchZonePins = async (zoneId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('audio_pins')
        .select('*')
        .eq('zone_id', zoneId);

      if (error) throw error;
      setZonePins(data || []);
      if (data && data.length > 0) {
        setSelectedPin(data[0]);
      } else {
        setSelectedPin(null);
      }
    } catch (error) {
      console.error('Error fetching zone pins:', error);
      setZonePins([]);
    } finally {
      setLoading(false);
    }
  };

  const handleZoneSelect = (selectedZone: Zone) => {
    setCurrentZone(selectedZone);
    fetchZonePins(selectedZone.id);
    setIsPlaying(false);
    onZoneSelect?.(selectedZone);
  };

  const handlePinSelect = (pin: AudioPin) => {
    setSelectedPin(pin);
    setIsPlaying(false);
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const currentZoneIndex = zones.findIndex(z => z.id === currentZone.id);
  const zoneColor = colors[currentZoneIndex % colors.length];

  const thumbnailUrl = selectedPin
    ? `https://img.youtube.com/vi/${selectedPin.youtube_video_id}/maxresdefault.jpg`
    : null;
  const embedUrl = selectedPin
    ? `https://www.youtube-nocookie.com/embed/${selectedPin.youtube_video_id}?autoplay=1&enablejsapi=1&playsinline=1&rel=0&modestbranding=1`
    : null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white shadow-2xl z-50 border-t border-gray-200">
      <div className="max-w-7xl mx-auto p-4">
        <div className="flex items-start gap-4">
          {zones.length > 1 && (
            <div className="w-64 flex-shrink-0 max-h-96 overflow-y-auto bg-gray-50 rounded-lg p-3">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                Zones at this location ({zones.length})
              </h4>
              <div className="space-y-2">
                {zones.map((z, index) => {
                  const isSelected = z.id === currentZone.id;
                  const color = colors[index % colors.length];

                  return (
                    <button
                      key={z.id}
                      onClick={() => handleZoneSelect(z)}
                      className={`w-full text-left p-3 rounded-lg transition-all ${
                        isSelected
                          ? 'bg-purple-100 border-2 border-purple-500'
                          : 'bg-white border-2 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                          style={{ backgroundColor: color }}
                        >
                          Z
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold truncate ${
                            isSelected ? 'text-purple-900' : 'text-gray-900'
                          }`}>
                            {z.name}
                          </p>
                        </div>
                      </div>
                      {z.description && (
                        <p className="text-xs text-gray-600 line-clamp-2 ml-10">
                          {z.description}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white flex-shrink-0"
                  style={{ backgroundColor: zoneColor }}
                >
                  <Radio className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-gray-900 truncate">{currentZone.name}</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <MapPin className="w-4 h-4" />
                    <span>Sound Zone</span>
                    {currentZone.playlist_id && (
                      <>
                        <span>•</span>
                        <Music className="w-4 h-4" />
                        <span>Has playlist</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {onAddSoundDrop && showAddButton && (
                  <button
                    onClick={() => onAddSoundDrop(currentZone.latitude, currentZone.longitude)}
                    className="px-3 py-2 bg-[#00c7b7] hover:bg-[#14f0d5] text-white rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
                    title="Add Sound Drop to this zone"
                  >
                    <Plus className="w-4 h-4" />
                    Add Sound Drop
                  </button>
                )}
                {canEdit && onEdit && (
                  <button
                    onClick={onEdit}
                    className="p-2 text-[#00c7b7] hover:bg-teal-50 rounded-lg transition-colors"
                    title="Edit zone"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {currentZone.description && (
              <p className="text-gray-600 mb-3 text-sm">{currentZone.description}</p>
            )}

            <div className="flex gap-4">
              {zonePins.length > 0 && (
                <div className="w-56 flex-shrink-0 max-h-80 overflow-y-auto bg-gray-50 rounded-lg p-3">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Music in this zone ({zonePins.length})
                  </h4>
                  <div className="space-y-2">
                    {zonePins.map((pin) => {
                      const isSelected = selectedPin?.id === pin.id;

                      return (
                        <button
                          key={pin.id}
                          onClick={() => handlePinSelect(pin)}
                          className={`w-full text-left p-2 rounded-lg transition-all ${
                            isSelected
                              ? 'bg-teal-100 border-2 border-[#00c7b7]'
                              : 'bg-white border-2 border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <p className={`text-sm font-semibold line-clamp-2 ${
                            isSelected ? 'text-blue-900' : 'text-gray-900'
                          }`}>
                            {pin.title}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex-1">
                {loading ? (
                  <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                    <p className="text-gray-500">Loading music...</p>
                  </div>
                ) : selectedPin ? (
                  <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
                    {!isPlaying ? (
                      <div className="relative w-full h-full group cursor-pointer" onClick={() => setIsPlaying(true)}>
                        <img
                          src={thumbnailUrl!}
                          alt={selectedPin.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = `https://img.youtube.com/vi/${selectedPin.youtube_video_id}/hqdefault.jpg`;
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-opacity-50 transition-all flex items-center justify-center">
                          <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                            <div className="w-0 h-0 border-l-[24px] border-l-white border-t-[14px] border-t-transparent border-b-[14px] border-b-transparent ml-1" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <iframe
                        src={embedUrl!}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    )}
                  </div>
                ) : (
                  <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <Music className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500">No music in this zone yet</p>
                    </div>
                  </div>
                )}

                {selectedPin && (
                  <div className="mt-3">
                    <h4 className="font-semibold text-gray-900">{selectedPin.title}</h4>
                    {selectedPin.description && (
                      <p className="text-sm text-gray-600 mt-1">{selectedPin.description}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
